/**
 * @fileoverview Order process tracking dialog component for workflow management.
 * Handles the creation and management of process tracking entries for orders
 * requiring specific workflow validation and monitoring.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import React, { useEffect, useState } from 'react';
import { toLocalTime } from '../../../../../components/ex_dayjs';
import { Button, Grid } from '@mui/material';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import MDialog from '../../../../../components/controls/MDialog';
import {
    Create, TextInput, required,
    BooleanInput,
    SelectInput,
    SaveButton,
    SimpleForm,
    minLength,
    maxLength,
    useNotify,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { convertDataForMSelect } from '../../../../../utils/common';
import { IData } from '../../../../../types';

/**
 * Tracking configuration name constant for process tracking workflows.
 * @type {string}
 * @constant
 */
const TRACKING_NAME = 'ProcessTracking';

const convertObjToArray = (obj) => {
    if (!obj) return [];
    let rs: any[] = [];
    Object.entries(obj).map((m: any) => {
        rs.push({ id: m[0], name: m[1] });
    });
    return rs;
};


const isZipCodeError = (value) => {
    return value.toLowerCase().includes('zipcode');
};

function getProcessTrackingData (order) {
    if (!order) return null;
    return order.ordersProcessTracking?.find((x: any) => x.category === TRACKING_NAME);
}

export const onConfigTrackingProcess = (order, record, notes, countProcessingAttempts) => {
    const { internal_status, tags, primary_account } = record; // record = processing order.
    let orderProcess = getProcessTrackingData(record) || record.orderProcessTracking;
    let isExist = !!orderProcess;
    if (primary_account && !primary_account.includes('@')) {
        return {
            isShow: isExist || false,
            required: false,
            isExist: isExist
        };
    }
    const TAGS_REQUIRED = ['Problem', 'Order Support'];
    let ignoredAutoNotes = ['Clicked process button', 'SEC ', 'PRIMARY ', 'Changed in-hand-date', 'Shipped ', 'Changed status', 'PROCESS TRACKING'];
    let isShowTracking = order
        && order.event_market !== 'Exact'
        && parseInt(internal_status) === 1
        && parseInt(order.internal_status) !== 1 /*Available Unshipped */
        && parseInt(order.internal_status) !== 2 /*Delayed Unshipped */
        && parseInt(order.internal_status) !== 4 /*Completed */
        && !TAGS_REQUIRED.some(tag => tags.includes(tag))
        // At least 2 purchase attempts failed
        && countProcessingAttempts >= 2
        && (notes || []).some(x => x.email?.includes('@') && (x.note_type === 0 || x.note_type === -99) && !ignoredAutoNotes.some(n => x.post.startsWith(n)));


    return {
        isShow: order && !!orderProcess || isShowTracking,
        required: isShowTracking && order && !orderProcess,
        isExist: isExist
    };
};

/** Props for {@link FormContent}. */
interface FormContentProps {
    /** Dialog data: the tracking record, dictionaries, and list of known process-tracking errors. */
    data: IData;
    /** Called with the current form values when the user chooses to skip tracking. */
    onSkip: (values: any) => void;
    /** Whether saving this tracking also saves the parent order (enables the Skip action / adjusts the Save label). */
    isSaveOrder: boolean;
    /** Called to close the dialog. */
    handleClose: (...args: any[]) => void;
}

const FormContent: React.FC<FormContentProps> = ({ data, onSkip, isSaveOrder, handleClose }) => {
    const { record, processTrackingDics, processTrackingErrs } = data;
    const { setValue, getValues, clearErrors } = useFormContext();
    const [ipRequired, setIPRequired] = useState<IData>({});
    const [otherStateRequired, setOtherStateRequired] = useState<IData>({});
    const [zipCodeRequired, setZipCodeRequired] = useState<IData>({});
    const [isOtherError, setOtherError] = useState(false);
    // State to manage visibility of conditional text input for "Others" IP Type option
    const [isOthersIPType, setOthersIPType] = useState(false);
    // State to manage validation requirement for "Others" IP Type custom input (tied to Remote Comp checkbox)
    const [othersIPTypeRequired, setOthersIPTypeRequired] = useState<IData>({});
    // State to manage visibility of the Remote Desktop ID input (shown only when Remote Comp is checked)
    const [isRemoteComp, setRemoteComp] = useState(false);

    const [dictionary, setDictionaries] = useState({
        ipTypes: [] as any[],
        browsers: [] as any[],
        accountTypes: [] as any[],
        cardBrands: [] as any[]
    });

    const trimmedRequired = [
        required(),
        (value) => {
            if (value && typeof value === 'string' && value.trim() === '') {
                return 'This field cannot be null or contain only whitespace.';
            }
            return undefined;
        }
    ];

    const zipCodeValidation = [
        required(),
        (value) => {
            if (!value) return undefined;
            const trimmedValue = value.toString().trim();

            // US ZIP code patterns: 12345 or 12345-6789
            const usZipPattern = /^[0-9]{5}(-[0-9]{4})?$/;

            // Canadian postal code pattern: A1A 1A1 or A1A1A1
            const caPostalPattern = /^[A-Za-z][0-9][A-Za-z][ -]?[0-9][A-Za-z][0-9]$/;

            if (usZipPattern.test(trimmedValue) || caPostalPattern.test(trimmedValue)) {
                return undefined;
            }

            return 'Please enter a valid ZIP code (US: 12345 or 12345-6789) or postal code (Canada: A1A 1A1)';
        }
    ];

    if (!record) return;

    const stateRequired = [required(), minLength(2), maxLength(2)];

    useEffect(() => {
        setDictionaries((prev) => ({
            ...prev,
            ipTypes: convertObjToArray(processTrackingDics.ip_types),
            browsers: convertObjToArray(processTrackingDics.browsers),
            cardBrands: convertObjToArray(processTrackingDics.card_brands),
            accountTypes: convertDataForMSelect(processTrackingDics.account_types)
        } as any));
        if (!record) return;
        if (record.otherError) setOtherError(true);
        // Initialize "Others" IP Type conditional input visibility based on existing record data
        if (record.othersIPType) setOthersIPType(true);
        // Initialize Remote Desktop ID conditional input visibility based on existing record data
        if (record.remote_Comp) setRemoteComp(true);
        if (!record.remote_Comp) {
            setIPRequired({ validate: trimmedRequired });
            setOthersIPTypeRequired({ validate: trimmedRequired });
        }
        if (!record.same_Billing_Venue) setOtherStateRequired({ validate: stateRequired });
        if (record.error && isZipCodeError(record.error)) {
            setZipCodeRequired({ validate: zipCodeValidation });
        }
        // Handle "Others" IP Type - check if IP Type is not in the standard list
        let selectedIPType = (processTrackingDics.ip_types && record.ip_Type) ?
            Object.keys(processTrackingDics.ip_types).find((key: any) => key === record.ip_Type) : null;
        if (record.ip_Type && !selectedIPType) {
            // Fix avoid mutation on record object
            setValue('othersIPType', record.ip_Type);
            setValue('ip_Type', 'Others');
            setOthersIPType(true);
        } else setOthersIPType(false);
    }, [record]);

    const onChangeBrowser = (e) => {
        const { value } = e.target;
        if (value === 'Stealth') { // Select Hydraproxy for ip_type
            setValue('ip_Type', dictionary.ipTypes.find((x: any) => x.id === 'Hydraproxy')?.id);
        }
    };

    const onChangeError = (e) => {
        if (e) {
            const { value } = e.target;
            if (isZipCodeError(value)) {
                setZipCodeRequired({ validate: zipCodeValidation });
            } else {
                setZipCodeRequired({});
                clearErrors('zipCode');
            }
            if (value === 'other') {
                setValue('otherError', '');
                setOtherError(true);
                return;
            }
        }
        setValue('otherError', '');
        setOtherError(false);
    };

    /**
     * Handler for IP Type dropdown changes.
     * Shows/hides conditional text input field when "Others" option is selected.
     * Clears the custom IP type value when a different option is selected.
     */
    const onChangeIPType = (e) => {
        if (e) {
            const { value } = e.target;
            if (value === 'Others') {
                setValue('othersIPType', '');
                setOthersIPType(true);
                return;
            }
        }
        setValue('othersIPType', '');
        setOthersIPType(false);
    };

    const onChangeRemoteComp = (e) => {
        const { checked } = e.target;
        if (checked) {
            setIPRequired({});
            setOthersIPTypeRequired({});
            clearErrors(['ip', 'ip_Type', 'othersIPType']);
            setRemoteComp(true);
        } else {
            setIPRequired({ validate: trimmedRequired });
            setOthersIPTypeRequired({ validate: trimmedRequired });
            // Hide and clear Remote Desktop ID so it isn't submitted when Remote Comp is off
            setValue('remote_Desktop_Id', record?.remote_Desktop_Id || '');
            setRemoteComp(false);
        }
    };

    const onChangeSameVenue = (e) => {
        const { checked } = e.target;
        if (checked) setOtherStateRequired({});
        else setOtherStateRequired({ validate: stateRequired });
    };

    const handleSkip = () => {
        const v = getValues();
        onSkip(v);
    };

    const allowSkip = () => {
        const formErrorValue = getValues('error');
        const isSkipTracking = !!formErrorValue && formErrorValue.toLowerCase() === 'tracking skipped';
        return isSaveOrder && isSkipTracking;
    };

    const ErrorSelectInput = () => (
        <Grid size={6}>
            <SelectInput
                source="error"
                validate={required()}
                choices={processTrackingErrs}
                onChange={onChangeError}
                translateChoice={false}
                resettable
                variant="outlined"
            />
        </Grid>
    );

    return (
        <div style={{ width: '100%' }}>
            <Grid container size={12} style={{ marginTop: -20, marginBottom: '10px' }}>
                <span><b>Author:&nbsp;</b>{record?.author}&nbsp;|&nbsp;<b>Tracking Time:&nbsp;</b>{record?.tracking_time}</span>
            </Grid>
            {/* Row 1 */}
            <Grid container spacing={2}>
                <Grid size={6}>
                    <TextInput source="ip" {...ipRequired} resettable variant="outlined" />
                </Grid>
                <Grid size={6}>
                    <SelectInput source="browser" validate={required()} choices={dictionary.browsers} onChange={onChangeBrowser} translateChoice={false} resettable variant="outlined" />
                </Grid>
            </Grid>
            {/* Row 2 */}
            <Grid container spacing={2}>
                <Grid size={6}>
                    <SelectInput label="IP Type" source="ip_Type" {...ipRequired} choices={dictionary.ipTypes} onChange={onChangeIPType} translateChoice={false} resettable value={record?.ip_Type} variant="outlined" />
                </Grid>
                <Grid size={6}>
                    <BooleanInput label="Incognito Browser" source="incognito_Browser" />
                </Grid>
            </Grid>
            {/* Conditional text input for "Others" IP Type - displays when "Others" is selected from IP Type dropdown */}
            {isOthersIPType && (
                <Grid container spacing={2}>
                    <Grid size={7}>
                        <TextInput source="othersIPType" label="Custom IP Type" {...othersIPTypeRequired} value={record?.othersIPType} resettable variant="outlined" />
                    </Grid>
                </Grid>
            )}
            {/* Row 3 */}
            <Grid container spacing={2}>
                <Grid size={6}>
                    <SelectInput label="Card Brand" source="card_Brand" validate={required()} choices={dictionary.cardBrands} translateChoice={false} resettable variant="outlined" />
                </Grid>
                <Grid size={6}>
                    <BooleanInput label="Same Billing/Venue Location" source="same_Billing_Venue" onChange={onChangeSameVenue} />
                </Grid>
            </Grid>
            {/* Row 4 */}
            <Grid container spacing={2}>
                <Grid size={6}>
                    <SelectInput label="Account Type" source="account_Type" validate={required()} choices={dictionary.accountTypes} translateChoice={false} resettable variant="outlined" />
                </Grid>
                <Grid size={6}>
                    <TextInput style={{ display: Object.keys(otherStateRequired).length === 0 ? 'none' : 'block' }}
                        label="Other State"
                        source="other_State"
                        {...otherStateRequired}
                        format={value => value?.toUpperCase()}
                        parse={value => value?.toUpperCase()}
                        placeholder="Format required: 2 letters. For instance, TX  not Texas"
                        resettable
                        variant="outlined" />
                </Grid>
            </Grid>
            {/* Row 5  */}
            <Grid container spacing={2}>
                {isRemoteComp ? (
                    <Grid size={6}>
                        <TextInput source="remote_Desktop_Id"
                            label="Remote Desktop ID (RD ID)"
                            value={record?.remote_Desktop_Id}
                            resettable
                            validate={trimmedRequired}
                            variant="outlined"/>
                    </Grid>
                ) : (
                    <ErrorSelectInput />
                )}
                <Grid size={6}>
                    <BooleanInput label="Remote Comp" source="remote_Comp" onChange={onChangeRemoteComp} />
                </Grid>
            </Grid>
            {isRemoteComp && (
                <Grid container spacing={2}>
                    <ErrorSelectInput />
                </Grid>
            )}

            {isOtherError && (
                <Grid container>
                    <TextInput source="otherError" validate={trimmedRequired} value={record.otherError} multiline minRows={2} resettable variant="outlined" />
                </Grid>
            )}
            {zipCodeRequired && (
                <Grid container spacing={2}>
                    <TextInput style={{ display: Object.keys(zipCodeRequired).length === 0 ? 'none' : 'block' }}
                        label="Zip Code"
                        source="zipCode"
                        {...zipCodeRequired}
                        placeholder="US ZIP: 12345 or 12345-6789 | Canada: A1A 1A1 or A1A1A1"
                        resettable
                        variant="outlined" />
                </Grid>
            )}
            <Grid container justifyContent={'flex-end'} spacing={1} style={{ marginBottom: -30 }}>
                {allowSkip() && <Grid><Button startIcon={<SkipNextIcon />} onClick={handleSkip} variant='outlined' color='secondary'>Skip & Save Order</Button></Grid>}
                {!allowSkip() && <Grid><SaveButton disabled={allowSkip()} variant="contained" color="primary" label={isSaveOrder ? 'Save Tracking & Order' : 'Save Tracking'} /></Grid>}
                <Grid><Button onClick={handleClose} variant='outlined' aria-label={'h_isOpen'}>Close</Button></Grid>
            </Grid>
        </div>
    );
};

/** Props for {@link OrderProcessTrackingPopup}. */
interface OrderProcessTrackingProps {
    /** Vendor configuration object. */
    vendor: any;
    /** Data provider for API operations. */
    dataProvider: any;
    /** Data containing order and dialog state information. */
    data: IData;
    /** Function to update order data. */
    setOrderData: (...args: any[]) => void;
    /** Fallback callback invoked with the saved process tracking record when `data.dlgState.callback` is not provided. */
    callback?: (...args: any[]) => void;
    /** Function to call when closing the dialog. */
    onClose: (...args: any[]) => void;
}

const OrderProcessTrackingPopup: React.FC<OrderProcessTrackingProps> = ({ vendor, dataProvider, data, setOrderData, callback, onClose }) => {
    const { order } = data;
    const { h_isOpen, h_isSaveOrder } = data.dlgState;
    const [isLoading, setLoading] = useState(false);
    const [trackingItem, setTrackingItem] = useState<any>();
    const [listError, setListError] = useState<any[]>([]);
    const notify = useNotify();
    const saveCallback = data.dlgState.callback || callback;

    useEffect(() => {
        if (order && h_isOpen) {
            const trackingErrors = [
                ...(order.orderProcessTrackingErrors || [])
                    .filter((x: any) => x.category === TRACKING_NAME)
                    .map((x: any) => ({
                        id: x.error,
                        name: x.market ? `${x.market}: ${x.error}` : x.error
                    })),
                { id: 'other', name: 'Other' }
            ];
            setListError(trackingErrors);
            let record = {
                order_id: order.id,
                order_number: order.order_number,
                category: TRACKING_NAME
            } as any;
            const orderTracking = order.orderProcessTracking || getProcessTrackingData(order);
            setOrderData(prev => ({ ...prev, orderProcessTracking: orderTracking }));
            if (orderTracking) {
                record = { ...record, ...orderTracking };
                record.tracking_time = record?.utc_Posted_On ? toLocalTime(record.utc_Posted_On).format('MMM D YYYY hh:mm A') : '';
                record.otherError = null;
                record.othersIPType = null;
                let selectedError = trackingErrors.find((x: any) => x.id === record.error);
                let _error = selectedError && selectedError.id;
                if (!_error) {
                    record.otherError = record.error;
                    record.error = 'other';
                }
            }
            setTrackingItem(record);
        }
    }, [order, h_isOpen]);

    const SaveProcessTracking = (data) => {
        setLoading(true);
        dataProvider.postData(vendor.controller, 'SaveProcessTracking', data)
            .then((response: any) => {
                setLoading(false);
                if (response && response.data?.success) {
                    onClose(null, 'h_isOpen', false);
                    notify('Saved Tracking successfully!', { type: 'success', autoHideDuration: 1000 });
                    saveCallback(response.data.orderProcess);
                } else {
                    notify('Saved Tracking has error', { type: 'error', autoHideDuration: 1000 });
                }
            });
    };

    const onSubmit = (data) => {
        if (data.otherError) {
            data.error = data.otherError.trim();
            data.zipCode = ''; // Clear zip code if other error
        } else if (!isZipCodeError(data.error)) data.zipCode = ''; // Case not other and not zip code error => clear zip code
        // Handle "Others" IP Type custom value submission
        if (data.othersIPType) {
            data.ip_Type = data.othersIPType.trim();
        }
        if (data.same_Billing_Venue) data.other_State = '';
        if (!data.remote_Comp) data.remote_Desktop_Id = null;
        SaveProcessTracking(data);
    };

    // only skip when error = 'tracking skipped'
    const onSkip = (data) => {
        // Update order note to save note automatically
        setOrderData((prev) => ({ ...prev, isSaveNote: true, newNote: 'PROCESS TRACKING - SKIPPED' }));
        // Remove all other unnecessary fields when skipping
        const cleanData = { ...data };
        delete cleanData.incognito_Browser;
        delete cleanData.same_Billing_Venue;
        delete cleanData.remote_Comp;
        delete cleanData.remote_Desktop_Id;
        SaveProcessTracking(cleanData);
    };

    return (
        <Create record={trackingItem} resource="Vivid" >
            <MDialog
                title={`PROCESS TRACKING #${trackingItem?.order_number}`}
                maxWidth={'md'}
                open={h_isOpen}
                handleClose={onClose}
                name={'h_isOpen'}
                footer={false}
                disabled={isLoading}
                msgLoading={'Saving...'}
                className={'order-dialog'}
                sx={{ '& .MuiDialog-paper': { width: '700px' } }}
            >
                <SimpleForm defaultValues={order?.orderProcessTracking || {}} toolbar={false} mode="onBlur" reValidateMode="onBlur" onSubmit={onSubmit}>
                    <FormContent
                        handleClose={onClose}
                        onSkip={onSkip}
                        isSaveOrder={h_isSaveOrder}
                        data={{
                            record: trackingItem,
                            processTrackingDics: order.orderProcessTrackingDictionaries || {},
                            processTrackingErrs: listError
                        }}
                    />
                </SimpleForm>
            </MDialog>
        </Create>
    );
};
export default React.memo(OrderProcessTrackingPopup);
